import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../config/api";
import { productService } from "../services/productService";
import Modal from "../components/ui/Modal";
import {
  normalizeNicheCategoryState,
  normalizeProductsResponse,
} from "../utils/productManagement";
import {
  Image as ImageIcon,
  Loader2,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";

const EMPTY_FORM = {
  categoryId: "",
  name: "",
  description: "",
  quantity: "",
  condition: "",
  isNegotiable: false,
  productImages: [],
};

const SORT_OPTIONS = [
  { value: "", label: "Default order" },
  { value: "latest", label: "Latest" },
  { value: "oldest", label: "Oldest" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "name_desc", label: "Name Z-A" },
  { value: "quantity_desc", label: "Highest quantity" },
  { value: "quantity_asc", label: "Lowest quantity" },
];

const PAGE_SIZE = 6;

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const arraysEqual = (left, right) => {
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

const readFilesAsDataUrls = (files) =>
  Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
          reader.readAsDataURL(file);
        }),
    ),
  );

const isDataUrl = (value) =>
  typeof value === "string" && value.startsWith("data:");

const getImageSummary = (value) =>
  isDataUrl(value) ? "Uploaded from device" : value;

type SaveProductPayload = {
  productId?: string;
  payload: Record<string, any>;
};

export default function Products() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const token = user?.token;
  const hasSellerAccount = Boolean(user?.seller?.id || user?.sellerAccountId);

  const [productsActionError, setProductsActionError] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    categoryId: "",
    condition: "",
    isNegotiable: "",
    sortBy: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [isReadingImages, setIsReadingImages] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const nicheQuery = useQuery({
    queryKey: ["my-niche-categories"],
    queryFn: async () =>
      normalizeNicheCategoryState(await productService.getMyNicheCategories()),
    enabled: Boolean(token),
  });

  const productsQuery = useQuery({
    queryKey: [
      "seller-products",
      filters.search,
      filters.categoryId,
      filters.condition,
      filters.isNegotiable,
      filters.sortBy,
    ],
    queryFn: async () =>
      normalizeProductsResponse(
        await productService.listMyProducts({
          search: filters.search || undefined,
          categoryId: filters.categoryId || undefined,
          condition: filters.condition || undefined,
          isNegotiable:
            filters.isNegotiable === ""
              ? undefined
              : filters.isNegotiable === "true",
          sortBy: filters.sortBy || undefined,
        }),
      ),
    enabled: Boolean(token) && hasSellerAccount && nicheQuery.isSuccess,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ productId, payload }: SaveProductPayload) => {
      if (productId) {
        return productService.updateProduct(productId, payload);
      }

      return productService.createProduct(payload);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (productId: string) => productService.deleteProduct(productId),
  });

  const nicheState = nicheQuery.data || normalizeNicheCategoryState({});
  const products = productsQuery.data || [];
  const hasLoadedNiche = nicheQuery.isFetched;
  const nicheLoading = Boolean(token) && nicheQuery.isLoading;
  const nicheError = !token
    ? ""
    : nicheQuery.error
      ? getApiErrorMessage(
          nicheQuery.error,
          "Could not load your niche categories.",
        )
      : "";
  const productsLoading =
    Boolean(token) && hasSellerAccount && productsQuery.isLoading;
  const productsError = !token
    ? ""
    : !hasSellerAccount
      ? "Seller account not available yet. Complete seller onboarding first."
      : productsQuery.error
        ? getApiErrorMessage(productsQuery.error, "Could not load products.")
        : productsActionError;

  const selectedCategories = nicheState.selectedCategories;
  const selectedCategoryIds = useMemo(
    () => new Set(selectedCategories.map((category) => category.id)),
    [selectedCategories],
  );

  const productLimitReached =
    nicheState.maxProducts > 0 && products.length >= nicheState.maxProducts;

  const canMutateProducts = !nicheState.isBanned;
  const canCreateProduct =
    canMutateProducts &&
    hasSellerAccount &&
    selectedCategories.length > 0 &&
    !productLimitReached &&
    !nicheLoading;

  const pageCount = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visibleProducts = products.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.categoryId,
    filters.condition,
    filters.isNegotiable,
    filters.search,
    filters.sortBy,
  ]);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      ...EMPTY_FORM,
      categoryId: selectedCategories[0]?.id || "",
    });
    setFormErrors({});
    setSubmitError("");
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      categoryId: product.categoryId || "",
      name: product.name || "",
      description: product.description || "",
      quantity: String(product.quantity ?? ""),
      condition: product.condition || "",
      isNegotiable: product.isNegotiable === true,
      productImages: product.productImages || [],
    });
    setFormErrors({});
    setSubmitError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setSubmitError("");
    setIsReadingImages(false);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const images = (formData.productImages || []).filter(Boolean);
    const categoryId = formData.categoryId.trim();
    const name = formData.name.trim();
    const description = formData.description.trim();
    const condition = formData.condition.trim().toUpperCase();
    const quantity = Number(formData.quantity);

    if (!categoryId || !selectedCategoryIds.has(categoryId)) {
      (errors as Record<string, string>).categoryId = "Choose one of your approved niche categories.";
    }

    if (!name) {
      (errors as Record<string, string>).name = "Product name is required.";
    }

    if (!description) {
      (errors as Record<string, string>).description = "Description is required.";
    }

    if (formData.quantity === "" || Number.isNaN(quantity) || quantity < 0) {
      (errors as Record<string, string>).quantity = "Quantity must be 0 or greater.";
    }

    if (!condition) {
      (errors as Record<string, string>).condition = "Condition is required.";
    }

    if (!editingProduct && images.length === 0) {
      (errors as Record<string, string>).productImages = "Select at least one product image.";
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return null;
    }

    return {
      categoryId,
      name,
      description,
      quantity,
      condition,
      isNegotiable: formData.isNegotiable,
      productImages: images,
    };
  };

  const handleImageSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    const invalidFile = files.find((file) => !(file as File).type.startsWith("image/"));
    if (invalidFile) {
      setSubmitError("Only image files can be selected for product images.");
      return;
    }

    setIsReadingImages(true);
    setSubmitError("");

    try {
      const imageDataUrls = await readFilesAsDataUrls(files);
      setFormData((prev) => ({
        ...prev,
        productImages: [...(prev.productImages || []), ...imageDataUrls],
      }));
      setFormErrors((prev) => ({ ...prev, productImages: undefined }));
    } catch (error) {
      setSubmitError(error.message || "Could not process selected images.");
    } finally {
      setIsReadingImages(false);
    }
  };

  const handleImageRemove = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      productImages: (prev.productImages || []).filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  const buildPatchPayload = (nextPayload) => {
    if (!editingProduct) {
      return nextPayload;
    }

    const patchPayload = {};

    if (nextPayload.categoryId !== editingProduct.categoryId) {
      (patchPayload as Record<string, any>).categoryId = nextPayload.categoryId;
    }

    if (nextPayload.name !== editingProduct.name) {
      (patchPayload as Record<string, any>).name = nextPayload.name;
    }

    if (nextPayload.description !== editingProduct.description) {
      (patchPayload as Record<string, any>).description = nextPayload.description;
    }

    if (nextPayload.quantity !== editingProduct.quantity) {
      (patchPayload as Record<string, any>).quantity = nextPayload.quantity;
    }

    if (nextPayload.condition !== editingProduct.condition) {
      (patchPayload as Record<string, any>).condition = nextPayload.condition;
    }

    if (nextPayload.isNegotiable !== editingProduct.isNegotiable) {
      (patchPayload as Record<string, any>).isNegotiable = nextPayload.isNegotiable;
    }

    if (
      !arraysEqual(nextPayload.productImages, editingProduct.productImages || [])
    ) {
      (patchPayload as Record<string, any>).productImages = nextPayload.productImages;
    }

    return patchPayload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token || saveMutation.isPending || !canMutateProducts || !hasSellerAccount) {
      return;
    }

    if (isReadingImages) {
      setSubmitError("Please wait for the selected images to finish loading.");
      return;
    }

    if (!editingProduct && productLimitReached) {
      setSubmitError("You have reached the product limit for your current plan.");
      return;
    }

    const nextPayload = validateForm();
    if (!nextPayload) {
      return;
    }

    const requestBody = editingProduct
      ? buildPatchPayload(nextPayload)
      : nextPayload;

    if (editingProduct && Object.keys(requestBody).length === 0) {
      setSubmitError("No changes detected.");
      return;
    }

    setSubmitError("");

    try {
      await saveMutation.mutateAsync({
        productId: editingProduct?.id,
        payload: requestBody,
      });

      closeModal();
      await queryClient.invalidateQueries({
        queryKey: ["seller-products"],
      });
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(
          error,
          editingProduct
            ? "Could not update this product."
            : "Could not create this product.",
        ),
      );
    }
  };

  const handleDelete = async (productId) => {
    if (!token || deletingId || !canMutateProducts) {
      return;
    }

    const confirmed = window.confirm(
      "Archive this product? It will be removed from your active listings.",
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(productId);
    setProductsActionError("");

    try {
      await deleteMutation.mutateAsync(productId);
      await queryClient.invalidateQueries({
        queryKey: ["seller-products"],
      });
    } catch (error) {
      setProductsActionError(
        getApiErrorMessage(error, "Could not archive this product."),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const summaryCards = [
    {
      label: "Products",
      value: String(products.length),
      hint:
        nicheState.maxProducts > 0
          ? `${products.length}/${nicheState.maxProducts} used`
          : "No plan cap returned",
    },
    {
      label: "Approved Categories",
      value: String(selectedCategories.length),
      hint:
        nicheState.maxCategories > 0
          ? `${nicheState.selectedCount}/${nicheState.maxCategories} selected`
          : `${nicheState.selectedCount} selected`,
    },
    {
      label: "Remaining Slots",
      value: String(Math.max(nicheState.remainingSelections, 0)),
      hint: "Available category selections",
    },
    {
      label: "Seller Status",
      value: nicheState.isBanned ? "Restricted" : "Active",
      hint: nicheState.shopName || user?.shopName || user?.name || "Seller",
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Product Management
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            Seller niche categories load first, then products load with your
            current filters. Product category choices are limited to approved
            niche categories only.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          disabled={!canCreateProduct}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
          <Plus size={16} /> Add Product
        </button>
      </div>

      {nicheState.isBanned && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          This seller is currently restricted. Product creation, edits, and
          deletion are disabled until the restriction is removed.
        </div>
      )}

      {!nicheState.isBanned && selectedCategories.length === 0 && hasLoadedNiche && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          No approved niche categories are selected yet. Update your niche
          categories before creating products.
        </div>
      )}

      {productLimitReached && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          You have reached the maximum product count allowed by your current
          plan.
        </div>
      )}

      {(nicheError || productsError) && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {nicheError || productsError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
              {card.label}
            </p>
            <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
              {card.value}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {card.hint}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.5fr)_repeat(4,minmax(0,1fr))]">
          <label className="relative block">
            <Search
              size={15}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={filters.search}
              onChange={(event) =>
                handleFilterChange("search", event.target.value)
              }
              placeholder="Search products"
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </label>

          <select
            value={filters.categoryId}
            onChange={(event) =>
              handleFilterChange("categoryId", event.target.value)
            }
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
            <option value="">All categories</option>
            {selectedCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <input
            value={filters.condition}
            onChange={(event) =>
              handleFilterChange("condition", event.target.value)
            }
            placeholder="Condition"
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
          />

          <select
            value={filters.isNegotiable}
            onChange={(event) =>
              handleFilterChange("isNegotiable", event.target.value)
            }
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
            <option value="">All pricing</option>
            <option value="true">Negotiable</option>
            <option value="false">Fixed price</option>
          </select>

          <select
            value={filters.sortBy}
            onChange={(event) => handleFilterChange("sortBy", event.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
            {SORT_OPTIONS.map((option) => (
              <option key={option.value || "default"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {nicheLoading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
          <Loader2 size={34} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading seller niche categories...
          </p>
        </div>
      ) : productsLoading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
          <Loader2 size={32} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading products from the seller endpoint...
          </p>
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-14 text-center dark:border-gray-700 dark:bg-gray-900">
          <Package
            size={42}
            className="mx-auto mb-4 text-gray-300 dark:text-gray-700"
          />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            No products found
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
            Create your first product once you have at least one approved niche
            category selected.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {visibleProducts.map((product) => (
            <article
              key={product.id}
              className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
                      {product.name}
                    </h3>
                    <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                      {product.categoryName}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {product.condition || "-"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                    {product.description || "No description provided."}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEditModal(product)}
                    disabled={!canMutateProducts}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(product.id)}
                    disabled={!canMutateProducts || deletingId === product.id}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30">
                    {deletingId === product.id ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Trash2 size={15} />
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <MetricCard label="Quantity" value={String(product.quantity)} />
                <MetricCard
                  label="Negotiation"
                  value={product.isNegotiable ? "Negotiable" : "Fixed"}
                />
                <MetricCard
                  label="Images"
                  value={String(product.productImages.length)}
                />
                <MetricCard
                  label="Updated"
                  value={formatDateTime(product.updatedAt)}
                />
              </div>

              {product.productImages.length > 0 && (
                <div className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-white dark:bg-gray-900">
                    {product.productImages[0] ? (
                      <img
                        src={product.productImages[0]}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageIcon size={18} className="text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
                      Primary Image
                    </p>
                    <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-300">
                      {getImageSummary(product.productImages[0])}
                    </p>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {products.length > PAGE_SIZE && (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm dark:border-gray-800 dark:bg-gray-900 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-gray-500 dark:text-gray-400">
            Showing {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, products.length)} of {products.length} products.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
              disabled={currentPage === 1}
              className="rounded-xl border border-gray-200 px-3 py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
              Previous
            </button>
            <span className="min-w-20 text-center font-semibold text-gray-700 dark:text-gray-200">
              Page {currentPage} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(page + 1, pageCount))}
              disabled={currentPage === pageCount}
              className="rounded-xl border border-gray-200 px-3 py-2 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
              Next
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProduct ? "Edit Product" : "Create Product"}
        size="lg">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Approved Category" error={formErrors.categoryId} helper="">  
              <select
                value={formData.categoryId}
                onChange={(event) =>
                  handleFormChange("categoryId", event.target.value)
                }
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
                <option value="">Select category</option>
                {selectedCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Quantity" error={formErrors.quantity} helper="">
              <input
                type="number"
                min="0"
                value={formData.quantity}
                onChange={(event) => handleFormChange("quantity", event.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Product Name" error={formErrors.name} helper="">
              <input
                value={formData.name}
                onChange={(event) => handleFormChange("name", event.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </Field>

            <Field label="Condition" error={formErrors.condition} helper="">
              <input
                value={formData.condition}
                onChange={(event) =>
                  handleFormChange("condition", event.target.value.toUpperCase())
                }
                placeholder="NEW"
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </Field>
          </div>

          <Field label="Description" error={formErrors.description} helper="">
            <textarea
              rows={4}
              value={formData.description}
              onChange={(event) =>
                handleFormChange("description", event.target.value)
              }
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </Field>

          <Field
            label="Product Images"
            helper="Choose product images from this device."
            error={formErrors.productImages}>
            <div className="space-y-3">
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-violet-300 bg-violet-50 px-4 py-4 text-sm font-semibold text-violet-700 transition hover:bg-violet-100 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-200 dark:hover:bg-violet-500/15">
                {isReadingImages ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ImageIcon size={16} />
                )}
                {isReadingImages ? "Loading images..." : "Select from device"}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelection}
                  className="hidden"
                />
              </label>

              {(formData.productImages || []).length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(formData.productImages || []).map((image, index) => (
                    <div
                      key={`${index}-${image.slice(0, 24)}`}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white dark:bg-gray-900">
                        <img
                          src={image}
                          alt={`Product ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          Image {index + 1}
                        </p>
                        <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                          {getImageSummary(image)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-rose-200 text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-400">
                  No product images selected yet.
                </div>
              )}
            </div>
          </Field>

          <label className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-200">
            <input
              type="checkbox"
              checked={formData.isNegotiable}
              onChange={(event) =>
                handleFormChange("isNegotiable", event.target.checked)
              }
              className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            Seller is open to negotiation for this product.
          </label>

          {submitError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
              {submitError}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || isReadingImages}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
              {saveMutation.isPending && <Loader2 size={15} className="animate-spin" />}
              {editingProduct ? "Save Changes" : "Create Product"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-950">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </p>
    </div>
  );
}

function Field({ label, error, helper, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </span>
      {children}
      {helper && !error && (
        <span className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
          {helper}
        </span>
      )}
      {error && (
        <span className="mt-2 block text-xs text-rose-500">{error}</span>
      )}
    </label>
  );
}
